import { NoSsr } from "@mui/base";
import { notEmpty } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, useMemo } from "react";

import { config } from "shared/data";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { HeaderTab } from "scmp-app/components/tracking/google-analytics-4/types";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { pathname as homePathName } from "scmp-app/pages/home";
import { pathname as plusPathName } from "scmp-app/pages/plus";
import { pathname as plusLearnMorePathName } from "scmp-app/pages/plus/learnmore";

import { Container, SCMPPlusTabsList } from "./styles";

type Props = {
  className?: string;
  isPlus: boolean;
};

export const SCMPPlusTabs: FunctionComponent<Props> = ({ className, isPlus }) => {
  const router = useRouter();
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const hasSCMPPlusAccess = asyncRosettaState?.result?.user?.hasSCMPPlusAccessRight ?? false;
  const plusTrialPath = `${config.rosetta.subscribeHost}/plus`;

  const plusTabPath = useMemo(() => {
    if (plusLearnMorePathName === router.pathname) return plusLearnMorePathName;

    if (!notEmpty(asyncRosettaState) || asyncRosettaState?.status === "loading") return "";
    if (!hasSCMPPlusAccess) return plusTrialPath;
    return plusPathName;
  }, [asyncRosettaState, hasSCMPPlusAccess, plusTrialPath, router.pathname]);

  const getTabClickHandler = (headerTab: HeaderTab) => () => {
    sendGA4Tracking({
      action: "click",
      category: "header",
      customized_parameters: {
        header: headerTab,
      },
      subcategory: "",
    });
  };

  return (
    <Container className={className}>
      <NoSsr>
        <SCMPPlusTabsList
          $isActive={!isPlus}
          $isPlus={isPlus}
          onClick={getTabClickHandler("scmp")}
          pathname={homePathName}
        >
          SCMP.COM
        </SCMPPlusTabsList>
        {plusTabPath && (
          <SCMPPlusTabsList
            $isActive={isPlus}
            $isPlus={isPlus}
            onClick={getTabClickHandler("scmp_plus")}
            pathname={plusTabPath}
          >
            SCMP <i>PLUS</i>
          </SCMPPlusTabsList>
        )}
      </NoSsr>
    </Container>
  );
};

SCMPPlusTabs.displayName = "SCMPPlusTabs";
